/* eslint-disable camelcase */
import { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
} from "@mui/material"
import {
  CloudOff,
  CloudDone,
  CheckCircle,
  HourglassTop,
  Refresh,
  Sync,
  History,
  LibraryBooks,
  Public,
  Warning,
} from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"
import dayjs from "dayjs"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Mantener consistencia con el tema móvil de alta visibilidad
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#ed6c02" }, success: { main: "#2E7D32" } },
})

const MisActasDelegado = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [activeTab, setActiveTab] = useState(0) // 0: Todas, 1: En Cola, 2: Enviadas
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // Repositorios de datos
  const [actasEnCola, setActasEnCola] = useState([])
  const [actasEnviadas, setActasEnviadas] = useState([])

  // Estados de Seguridad y RLS
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // ========================================================
  // 1. MONITOREO DE RED Y CARGA INICIAL
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarHistorialCompleto()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const cargarHistorialCompleto = async () => {
    setIsLoading(true)
    setErrorAcceso(null)
    try {
      // A. Cargar datos locales de IndexedDB (Siempre disponible)
      const colaLocal = (await localforage.getItem("actas_offline_queue")) || []
      setActasEnCola(colaLocal)

      // B. Cargar datos del servidor SQL si hay red
      if (!navigator.onLine) {
        throw new Error("Offline")
      } else {
        const res = await api.post("/ControlElectoral/getActasDelegado", {})
        const data = res?.data !== undefined ? res.data : res

        if (data && data.success === false) {
          setErrorAcceso(data.message)
          return
        }

        if (data && data.success) {
          const actasReales = data.data || []
          const recintoAsignado = data.recinto || ""
          const isGlobalBackend = data.es_global || false

          setActasEnviadas(actasReales)
          setRecintoActual(recintoAsignado)
          setEsGlobal(isGlobalBackend)

          // Guardar en caché para disponibilidad offline
          await localforage.setItem("mis_actas_enviadas_cache", actasReales)
          await localforage.setItem("mis_actas_recinto_cache", recintoAsignado)
          await localforage.setItem("mis_actas_global_cache", isGlobalBackend)
        }
      }
    } catch (err) {
      console.log("Cargando historial desde memoria caché...")
      if (err?.response?.status === 403) {
        setErrorAcceso(err.response.data.message || "Usuario no autorizado.")
      } else {
        const aCache = (await localforage.getItem("mis_actas_enviadas_cache")) || []
        const rCache = (await localforage.getItem("mis_actas_recinto_cache")) || ""
        const gCache = (await localforage.getItem("mis_actas_global_cache")) || false

        setActasEnviadas(aCache)
        setRecintoActual(rCache)
        setEsGlobal(gCache)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // 2. MOTOR DE SINCRONIZACIÓN (REUTILIZADO DESDE INGRESO)
  // ========================================================
  const handleSincronizarManual = async () => {
    if (isOffline) {
      return Swal.fire("Aún sin señal", "Busque una zona abierta o ventana con cobertura celular.", "warning")
    }
    if (actasEnCola.length === 0) return

    setIsSyncing(true)
    Swal.fire({
      title: "Sincronizando...",
      text: `Transmitiendo ${actasEnCola.length} actas al Centro de Cómputo`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    })

    let exitosas = 0
    let colaRestante = [...actasEnCola]

    for (let i = 0; i < actasEnCola.length; i++) {
      try {
        const res = await api.post("/recepcion/acta", actasEnCola[i])
        const data = res?.data !== undefined ? res.data : res
        if (data.success) {
          exitosas++
          colaRestante = colaRestante.filter((a) => a.junta_codigo !== actasEnCola[i].junta_codigo)
        }
      } catch (err) {
        console.error("Error transmitiendo acta encolada:", err)
      }
    }

    await localforage.setItem("actas_offline_queue", colaRestante)
    setActasEnCola(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("¡Éxito Absoluto!", `Se transmitieron las ${exitosas} actas pendientes con éxito.`, "success")
    } else {
      Swal.fire(
        "Sincronización Parcial",
        `Se enviaron ${exitosas} actas. ${colaRestante.length} siguen encoladas por inestabilidad.`,
        "warning",
      )
    }

    cargarHistorialCompleto()
  }

  // ========================================================
  // 3. LOGICA DE FILTRADO
  // ========================================================
  const actasCombinadas = [
    ...actasEnCola.map((a) => ({
      ...a,
      estado_visual: "COLA",
      fecha_visual: a.timestamp,
    })),
    ...actasEnviadas.map((a) => ({
      junta_codigo: a.junta_codigo,
      junta_nombre: `Junta ${a.junta_numero} - ${a.junta_genero === "M" ? "Masculino" : "Femenino"}`,
      reci_codigo: a.reci_codigo, // Capturamos el recinto para modo Global
      sufragantes: a.acta_sufragantes,
      estado_visual: a.acta_estado,
      fecha_visual: a.aud_fecisys,
    })),
  ]

  const actasFiltradas = actasCombinadas.filter((acta) => {
    if (activeTab === 1) return acta.estado_visual === "COLA"
    if (activeTab === 2) return acta.estado_visual === "CUADRADA" || acta.estado_visual === "NOVEDAD"
    return true
  })

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        <Box display="flex" justifyContent="center" alignItems="center" my={2} color="primary.main">
          <History sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            Historial de Actas
          </Typography>
        </Box>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "white",
          }}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? <CloudOff color="error" sx={{ mr: 1 }} /> : <CloudDone color="success" sx={{ mr: 1 }} />}
            <Typography variant="body2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "TRABAJANDO SIN RED" : "CONEXIÓN ESTABLE"}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={cargarHistorialCompleto}
              disabled={isLoading || isSyncing}
              size="small"
              sx={{ border: "1px solid #ccc" }}
            >
              <Refresh fontSize="small" />
            </IconButton>
            {actasEnCola.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<Sync />}
                onClick={handleSincronizarManual}
                disabled={isOffline || isSyncing}
              >
                SUBIR ({actasEnCola.length})
              </Button>
            )}
          </Box>
        </Paper>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ mt: 2, borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            {/* INDICADOR DE COBERTURA (RLS) */}
            {recintoActual && (
              <Box display="flex" alignItems="center" mb={2} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
                <Typography
                  variant="subtitle2"
                  color="primary.dark"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  📍 Cobertura: {recintoActual}
                </Typography>
                {esGlobal && (
                  <Chip
                    icon={<Public fontSize="small" />}
                    label="MODO GLOBAL"
                    color="info"
                    size="small"
                    sx={{ ml: 2, fontWeight: "bold" }}
                  />
                )}
              </Box>
            )}

            <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="TODAS" sx={{ fontWeight: "bold" }} />
                <Tab
                  label={`EN COLA (${actasEnCola.length})`}
                  sx={{ fontWeight: "bold", color: actasEnCola.length > 0 ? "#ed6c02" : "inherit" }}
                />
                <Tab label={`ENVIADAS (${actasEnviadas.length})`} sx={{ fontWeight: "bold" }} />
              </Tabs>
            </Paper>

            {isLoading ? (
              <Box display="flex" justifyContent="center" my={5}>
                <CircularProgress />
              </Box>
            ) : actasFiltradas.length === 0 ? (
              <Box textAlign="center" my={5} p={3}>
                <LibraryBooks sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
                <Typography color="textSecondary" fontStyle="italic">
                  No registra actas en esta bandeja.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {actasFiltradas.map((acta, idx) => {
                  let chipLabel = "Enviada"
                  let chipColor = "success"
                  let chipIcon = <CheckCircle />

                  if (acta.estado_visual === "COLA") {
                    chipLabel = "En Cola (Offline)"
                    chipColor = "warning"
                    chipIcon = <HourglassTop />
                  } else if (acta.estado_visual === "NOVEDAD") {
                    chipLabel = "Con Novedad"
                    chipColor = "error"
                    chipIcon = <Warning />
                  }

                  return (
                    <Grid item xs={12} key={idx}>
                      <Card
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          borderLeft: `6px solid ${acta.estado_visual === "COLA" ? "#ed6c02" : "#2E7D32"}`,
                        }}
                      >
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {acta.junta_nombre || `Mesa Código: ${acta.junta_codigo}`}
                              </Typography>

                              {/* Si es Admin, mostrar de qué recinto viene esta acta */}
                              {esGlobal && acta.reci_codigo && (
                                <Typography variant="caption" color="primary.main" fontWeight="bold" display="block">
                                  Recinto: {acta.reci_codigo}
                                </Typography>
                              )}

                              <Typography variant="caption" color="textSecondary">
                                Registrado: {dayjs(acta.fecha_visual).format("DD/MM/YYYY HH:mm:ss")}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              icon={chipIcon}
                              label={chipLabel}
                              color={chipColor}
                              sx={{ fontWeight: "bold" }}
                            />
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="textSecondary">
                              Total Votos Registrados:
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="primary.main"
                              sx={{ fontSize: "1.1rem" }}
                            >
                              {acta.sufragantes} sufragios
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default MisActasDelegado
